// DO ROTEIRIZADOR PARA O ACERVO — a volta que faltava, e ela é estreita de propósito.
//
// ── O QUE EU MEDI, E É O MOTIVO DESTE ARQUIVO ───────────────────────────────────────────────
//
// `T2.16` pede que "escrever e deixar montar" passe pelo login. Ao medir para responder isso, o
// que apareceu foi pior do que o ponto: **`/roteirizar` não tem caminho de salvar nenhum.** As
// três únicas menções a conta no arquivo eram `Id()`, `Name()` e `EducationalInstitution()`, para
// preencher o campo de autor do documento gerado. Nenhum botão, nenhuma escrita.
//
// Ou seja: a criança escreve a história, espera os quadros serem desenhados um a um, vê o
// quadrinho pronto na tela — e ele existe enquanto a aba estiver aberta. Fechou, acabou.
//
// E a frase do `App.tsx` — *"a rota é pública porque escrever uma história não precisa de conta;
// SALVAR PRECISA, e é aí que o login entra"* — descrevia uma INTENÇÃO, não uma implementação. O
// login não entrava em lugar nenhum porque não havia salvar.
//
// ── POR QUE UMA PONTE DE VOLTA, SE O `converter.ts` DIZ QUE ELA NÃO EXISTE ──────────────────
//
// O `converter.ts` diz, e continua certo: *"publicar é um RETRATO. O que está na biblioteca não
// volta para o editor (...) uma ponte de mão dupla exigiria que os dois formatos fossem
// equivalentes, e eles não são."*
//
// Esta ponte é OUTRA, e a diferença é o que a torna honesta: ela não traz de volta um documento
// publicado qualquer — traz o subconjunto que o ROTEIRIZADOR acabou de gerar, e esse subconjunto
// é conhecido e pequeno. Quadro, imagem e balão de fala, que é exatamente o que `roteiro/` monta.
// Tudo o que o v2 sabe e o Konva não — motor, legenda, camada nomeada, retícula — é perdido aqui,
// e perder isso num documento recém-gerado não perde nada que alguém tenha feito à mão.
//
// O que NÃO tem correspondente vira `null` e é contado em `perdidos`, para a tela poder dizer em
// voz alta o que não foi junto. Sumir em silêncio é o defeito que este ticket já pagou uma vez.
//
// ── SEM KONVA AQUI DENTRO ───────────────────────────────────────────────────────────────────
//
// Este módulo produz o OBJETO que o Firestore guarda, e não instâncias de `DraggableItem`. Duas
// razões: o editor já sabe montar as instâncias a partir do objeto (`DraggableItemFactory`), e a
// cadeia do Konva não carrega sob o jest — a mesma razão pela qual `itens-da-pagina.ts` foi
// extraído. Uma conversão que não dá para testar é uma conversão em que ninguém confia.

import { DocumentoHQ, Elemento, Pagina } from "../api/documento.types";
import { estiloOuFala } from "../classes/balao-do-estilo";

/** O formato que o Firestore guarda para um item — `DraggableItemObj`, sem depender do Konva. */
export interface ItemDoEditor {
  id: string;
  type: "characters" | "rectangles" | "scenarios" | "textBubbles" | "figures";
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  mirror: boolean;
  imgId: string;
  // Só para `textBubbles`.
  text?: string;
  fontSize?: number;
  fontFamily?: string;
  underline?: boolean;
  italic?: boolean;
  bold?: boolean;
  /**
   * O estilo do balão, para ele poder ser DESENHADO quando não há imagem (`T6.5`).
   *
   * Nasceu aqui porque nasceu no roteirizador: `documento.types.ts` já tinha `estilo` no `Balao`, e
   * esta travessia o descartava. Guardá-lo no item do editor é o que faz um quadrinho salvo hoje
   * continuar com balões de forma certa quando for reaberto amanhã.
   */
  estilo?: string;

  // Só para `rectangles` — o quadro. Sem estes três o `Rectangle` desenha `stroke={undefined}`,
  // que é um quadro sem contorno, e era a outra metade do `T6.5`.
  borderWidth?: number;
  borderColor?: string;
  fillColor?: string;
}

/** Uma página, como o `Layer.toObj()` a escreve. */
export interface PaginaDoEditor {
  width: number;
  height: number;
  items: ItemDoEditor[];
}

/** O documento inteiro, como o `Comic.toObj()` o escreve. */
export interface QuadrinhoDoEditor {
  name: string;
  /** Dicionário por número de página — `Utils.arrToDict` produz exatamente isto. */
  pages: Record<number, PaginaDoEditor>;
}

export interface ResultadoDaConversao {
  quadrinho: QuadrinhoDoEditor;
  /** O que não tem correspondente no editor, contado por tipo, para a tela poder dizer. */
  perdidos: Record<string, number>;
}

/**
 * A fonte da imagem de um elemento, resolvida por quem chama.
 *
 * O editor referencia imagem por `imgId` — uma chave no catálogo privado da conta. O documento
 * gerado traz a imagem inteira em base64 dentro de `refImagem`. Quem chama é que sabe subir a
 * imagem e devolver o id; aqui só entra o mapa pronto, para esta peça continuar pura.
 */
export type IdDaImagem = (refImagem: string) => string | undefined;

/** O traço de um quadro, quando o documento não disser. É o que o roteirizador grava hoje. */
const TRACO_DO_QUADRO = 4;

/** A cor do traço, quando o documento não disser. */
const COR_DO_TRACO = "#000000";

const FONTE_PADRAO = "Fredoka";
const CORPO_PADRAO = 16;

function itemDe(
  elemento: Elemento,
  indice: number,
  idDaImagem: IdDaImagem
): ItemDoEditor | null {
  const base = {
    id: elemento.id || `el-${indice}`,
    x: Math.round(elemento.x ?? 0),
    y: Math.round(elemento.y ?? 0),
    width: Math.round(elemento.largura ?? 0),
    height: Math.round(elemento.altura ?? 0),
    rotation: Math.round(elemento.rotacao ?? 0),
    mirror: false,
    imgId: "",
  };

  if (elemento.tipo === "balao" || elemento.tipo === "texto") {
    return {
      ...base,
      type: "textBubbles",
      // ── O ESTILO ATRAVESSA, E É O CONSERTO DO T6.5 ────────────────────────────────────────
      //
      // Ele: *"saiu sem formatação de quadrinho, de fala. Tá só um texto."* E era literal: o
      // `TextBubble` do editor desenha a forma do balão como IMAGEM, e aqui o `imgId` ficava "".
      // Sem id, sem imagem; sem imagem, só o texto.
      //
      // O roteirizador já sabia o estilo de cada balão — `fala`, `narracao`, `grito`,
      // `pensamento`, `sussurro` — e esta função o jogava fora. Levando-o adiante,
      // `itens-da-pagina.ts` desenha a forma certa em SVG (`balao-do-estilo.ts`) e ela entra
      // pelo mesmo caminho de imagem que já existe.
      estilo: estiloOuFala((elemento as { estilo?: unknown }).estilo),
      text: elemento.texto || "",
      fontSize: Math.round(elemento.tamanhoFonte ?? CORPO_PADRAO),
      fontFamily: elemento.fonte || FONTE_PADRAO,
      underline: false,
      italic: false,
      bold: false,
    };
  }

  if (elemento.tipo === "imagem") {
    const imgId = idDaImagem(elemento.refImagem);
    // SEM ID, O ITEM CONTINUA. É a mesma lição do `itens-da-pagina.ts`: um item sem imagem nasce
    // com URL vazia e a criança vê um lugar vazio que dá para preencher. Descartado, ele some do
    // documento e o desenho dela morre no primeiro save.
    return { ...base, type: "figures", imgId: imgId || "" };
  }

  if (elemento.tipo === "quadro") {
    // ── O CONTORNO DO QUADRO TAMBÉM SE PERDIA (T6.5) ─────────────────────────────────────────
    //
    // Era `{ ...base, type: "rectangles" }`, e o `Rectangle` desenha
    // `stroke={this.borderColor} strokeWidth={this.borderWidth}`. Nenhum dos dois era passado,
    // então os dois chegavam `undefined` e o quadro saía SEM CONTORNO — a outra metade do "sem
    // formatação de quadrinho".
    //
    // E o documento do roteirizador CARREGA os dois: `traco: 4` e `corTraco: "#000000"`, gravados
    // em toda página que ele monta. Ninguém os lia.
    //
    // `fillColor` transparente e não branco: um quadro branco por cima taparia o desenho que o
    // roteirizador põe DENTRO dele — a ordem dos elementos na camada não garante que o quadro
    // venha antes da imagem.
    return {
      ...base,
      type: "rectangles",
      borderWidth: Math.round(elemento.traco ?? TRACO_DO_QUADRO),
      borderColor: elemento.corTraco || COR_DO_TRACO,
      fillColor: "transparent",
    };
  }

  // `forma` e `personagem`: o editor tem classes para os dois, mas o que as identifica não é a
  // caixa — é a imagem do acervo compartilhado, que este documento não carrega. Inventar uma
  // aproximação aqui poria na página da criança um elemento que ela não pediu.
  return null;
}

function paginaDe(pagina: Pagina, idDaImagem: IdDaImagem, perdidos: Record<string, number>) {
  const items: ItemDoEditor[] = [];
  let indice = 0;

  for (const camada of pagina.camadas || []) {
    // AS CAMADAS ACHATAM, e a ordem de desenho é preservada: o editor tem uma lista só por
    // página, e nela quem vem depois fica por cima — que é a mesma regra das camadas do v2.
    for (const elemento of camada.elementos || []) {
      const item = itemDe(elemento, indice++, idDaImagem);
      if (item) items.push(item);
      else perdidos[elemento.tipo] = (perdidos[elemento.tipo] || 0) + 1;
    }
  }

  return { width: Math.round(pagina.largura), height: Math.round(pagina.altura), items };
}

/** Converte o documento gerado no objeto que o acervo guarda. */
export function paraOEditor(
  documento: DocumentoHQ,
  idDaImagem: IdDaImagem = () => undefined
): ResultadoDaConversao {
  const perdidos: Record<string, number> = {};
  const pages: Record<number, PaginaDoEditor> = {};

  (documento.paginas || []).forEach((pagina, i) => {
    pages[i] = paginaDe(pagina, idDaImagem, perdidos);
  });

  return {
    quadrinho: {
      // O nome não pode ser vazio: `Comic.save()` lança "Nome inválido" com string vazia, e a
      // criança receberia um erro técnico por não ter dado título a uma história que ela mandou
      // a plataforma inventar.
      name: (documento.titulo || "").trim() || "História sem título",
      pages,
    },
    perdidos,
  };
}

/**
 * As imagens únicas do documento, na ordem em que aparecem.
 *
 * Únicas porque o mesmo desenho pode estar em mais de um quadro, e subir duas vezes a mesma
 * imagem em base64 dobra o peso do catálogo da conta sem nenhum ganho.
 */
export function imagensDoDocumento(documento: DocumentoHQ): string[] {
  const vistas = new Set<string>();
  for (const pagina of documento.paginas || []) {
    for (const camada of pagina.camadas || []) {
      for (const elemento of camada.elementos || []) {
        if (elemento.tipo === "imagem" && elemento.refImagem) vistas.add(elemento.refImagem);
      }
    }
  }
  // `Array.from` e nao espalhamento: o alvo de compilacao deste projeto e anterior ao es2015 e
  // nao itera Set com `...`. Foi o compilador que disse, e nao eu que supus.
  return Array.from(vistas);
}
